import { CircleAlert, CircleSlash, LucideIcon, Unplug } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AIActionHealthStatus } from '@/Domain/api/Action/dto/Action.dto';
import { BadgeVariantProps, badgeVariants } from '@/Domain/components/Shared/Badge/Badge';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { formatRelativeTime } from '@/Domain/helper/RelativeTime.helper';
import { getSkipReasonText } from '@/Domain/helper/actionHealth.helper';
import { ActionHealth } from '@/Domain/hooks/Action/useActionHealth.hook';
import { cn } from '@/Domain/utils/cn';

type Props = {
  health: ActionHealth;
  onOpenLogs?: () => void;
};

const BADGE_STYLE: Record<
  Exclude<AIActionHealthStatus, 'ok'>,
  { variant: BadgeVariantProps['variant']; Icon: LucideIcon }
> = {
  failing: { variant: 'error', Icon: CircleAlert },
  not_running: { variant: 'warning', Icon: CircleSlash },
  needs_reconnect: { variant: 'warning', Icon: Unplug },
};

export const ActionHealthBadge = ({ health, onOpenLogs }: Props) => {
  const { t } = useTranslation('actions_page');

  if (health.health === 'ok') return null;

  const { variant, Icon } = BADGE_STYLE[health.health];
  const labels = {
    failing: t('health.failing'),
    not_running: t('health.not_running'),
    needs_reconnect: t('health.needs_reconnect'),
  };
  const failure = health.last_failure;
  const skipReason = getSkipReasonText(health);

  return (
    <div className="flex items-center gap-1">
      <Tooltip>
        <TooltipTrigger
          type="button"
          data-testid="action-box-health-badge"
          className={cn(
            badgeVariants({ variant }),
            'cursor-help focus:ring-0 focus:ring-offset-0',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          )}
        >
          <Icon className="size-3" />
          {labels[health.health]}
        </TooltipTrigger>
        <TooltipContent className="max-w-xs space-y-1" data-testid="action-box-health-reason">
          {health.health === 'failing' && (
            <>
              <p className="font-medium">
                {t('health.failing_reason', {
                  count: health.recent_calls,
                  failed: health.recent_failed_calls,
                  total: health.recent_calls,
                })}
              </p>
              {failure && (
                <p className="text-muted-foreground">
                  {failure.http_status_code != null
                    ? t('health.http_status', { code: failure.http_status_code })
                    : t('logs.no_response')}
                  {` · ${formatRelativeTime(failure.created_at)}`}
                </p>
              )}
              {failure?.error_message && (
                <p className="line-clamp-3 break-words">{failure.error_message}</p>
              )}
            </>
          )}
          {health.health === 'not_running' && (
            <>
              <p className="font-medium">
                {t('health.not_running_reason', {
                  count: health.skipped_conversations,
                  days: health.window_days,
                })}
              </p>
              {skipReason && <p>{skipReason}</p>}
            </>
          )}
          {health.health === 'needs_reconnect' && <p>{t('health.skip_reason.needs_reconnect')}</p>}
        </TooltipContent>
      </Tooltip>
      {onOpenLogs && health.health !== 'needs_reconnect' && (
        <Button
          size="xxs"
          variant="ghost"
          className="text-foreground-subtle"
          data-testid="action-box-health-open-logs"
          onClick={onOpenLogs}
        >
          {t('health.view_details')}
        </Button>
      )}
    </div>
  );
};
